import { BUSINESS_THEMES, BUSINESS_TYPE_OPTIONS } from "../../platform/config";
import type { BusinessTypeKey } from "../../platform/config";
import React from "react";
import { Chip, Tooltip } from "@mui/material";
import { format } from "date-fns";
import {
  PLATFORM_LABELS,
  PlatformKey,
  SUBSCRIPTION_STATUS_COLORS,
  SUBSCRIPTION_STATUS_LABELS,
  SubscriptionPaymentRow,
  SubscriptionSummary,
} from "../../services/superadminService";

const chipSx = { fontSize: "0.72rem", height: 22, fontWeight: 600 } as const;

/** "12 days left", "Ended 3 Oct 2026", "No end date". */
export const describeSubscriptionEnd = (summary: SubscriptionSummary) => {
  if (!summary.endsAt) return "No end date";
  const date = format(new Date(summary.endsAt), "d MMM yyyy");
  if (summary.status === "EXPIRED") return `Ended ${date}`;
  const days = summary.daysLeft ?? 0;
  return `${days} day${days === 1 ? "" : "s"} left · ${date}`;
};

/** Status in words and colour, so it never relies on colour alone. */
export const SubscriptionStatusChip: React.FC<{
  summary: SubscriptionSummary;
}> = ({ summary }) => {
  const colors = SUBSCRIPTION_STATUS_COLORS[summary.status];
  const label =
    summary.status === "TRIAL" ||
    summary.status === "EXPIRING" ||
    summary.status === "GRACE"
      ? `${SUBSCRIPTION_STATUS_LABELS[summary.status]} · ${summary.daysLeft}d`
      : SUBSCRIPTION_STATUS_LABELS[summary.status];

  return (
    <Tooltip title={describeSubscriptionEnd(summary)}>
      <Chip
        size="small"
        label={label}
        sx={{ ...chipSx, bgcolor: colors.bg, color: colors.fg }}
      />
    </Tooltip>
  );
};

// Self-serve organizations also show their business type, in its colour.
export const PlatformChip: React.FC<{ platform: PlatformKey; businessType?: BusinessTypeKey | null }> = ({
  platform,
  businessType,
}) => {
  const type = platform === "CLINIC" && businessType ? BUSINESS_TYPE_OPTIONS.find((o) => o.key === businessType) : undefined;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={type ? `${PLATFORM_LABELS[platform]} · ${type.name}` : PLATFORM_LABELS[platform] ?? platform}
      sx={{
        ...chipSx,
        ...(platform === "CLINIC"
          ? type
            ? { color: BUSINESS_THEMES[type.key].main, borderColor: BUSINESS_THEMES[type.key].main }
            : { color: "#0E6A7E", borderColor: "#9ED3DF" }
          : { color: "#3F4753", borderColor: "#D5D9DE" }),
      }}
    />
  );
};

const PAYMENT_STATUS_STYLE: Record<
  SubscriptionPaymentRow["status"],
  { label: string; bg: string; fg: string }
> = {
  PAID: { label: "Paid", bg: "#E3F4EA", fg: "#17663F" },
  PENDING: { label: "Pending", bg: "#FDF1DC", fg: "#8A5A00" },
  FAILED: { label: "Failed", bg: "#FBE5E3", fg: "#A32B22" },
};

export const PaymentStatusChip: React.FC<{
  status: SubscriptionPaymentRow["status"];
}> = ({ status }) => {
  const style = PAYMENT_STATUS_STYLE[status] ?? PAYMENT_STATUS_STYLE.PENDING;
  return (
    <Chip
      size="small"
      label={style.label}
      sx={{ ...chipSx, bgcolor: style.bg, color: style.fg }}
    />
  );
};

/** The customer name for an invoice PDF, without the "(Deleted)" marker. */
export const invoiceCustomerName = (payment: SubscriptionPaymentRow, fallback = "") =>
  (payment.organization?.name ?? fallback).replace(/ \(Deleted\)$/, "");

/** "29 USD", "1,500,000 LBP". */
export const formatMoney = (amount: number, currency: string) =>
  `${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })} ${currency}`;
